import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './style.css';

// Canvas로 크롭된 이미지를 Blob으로 변환
function getCroppedImg(imageSrc, pixelCrop) {
    return new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = pixelCrop.width;
            canvas.height = pixelCrop.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, pixelCrop.width, pixelCrop.height,
            );
            canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
        };
        image.src = imageSrc;
    });
}

export default function AdminSlides() {
    const [slides, setSlides] = useState([]);
    const [slideLabel, setSlideLabel] = useState('');
    const [slideFile, setSlideFile] = useState(null);
    const [slideUploading, setSlideUploading] = useState(false);
    const [message, setMessage] = useState(null);

    // 크롭
    const [cropImage, setCropImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedPreview, setCroppedPreview] = useState(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3500);
    };

    const loadSlides = useCallback(() => {
        fetch('/api/admin/slides', { credentials: 'include' })
            .then(res => res.json())
            .then(data => { if (data.success) setSlides(data.slides); })
            .catch(() => {});
    }, []);

    useEffect(() => { loadSlides(); }, [loadSlides]);

    // 크롭
    const handleFileSelect = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        setCropImage(url);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCroppedPreview(null);
        setSlideFile(null);
        setCropModalOpen(true);
    };

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropConfirm = async () => {
        if (!cropImage || !croppedAreaPixels) return;
        const blob = await getCroppedImg(cropImage, croppedAreaPixels);
        const file = new File([blob], 'cropped-slide.png', { type: 'image/png' });
        setSlideFile(file);
        setCroppedPreview(URL.createObjectURL(blob));
        setCropModalOpen(false);
    };

    const handleCropCancel = () => {
        setCropModalOpen(false);
        setCropImage(null);
        const fi = document.getElementById('slideFile');
        if (fi) fi.value = '';
    };

    const handleSlideUpload = async () => {
        if (!slideLabel.trim()) return showMessage('error', '라벨을 입력하세요.');
        if (!slideFile) return showMessage('error', '이미지를 선택하세요.');
        setSlideUploading(true);
        try {
            const formData = new FormData();
            formData.append('files', slideFile);
            const uploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=biz_antsnest', {
                method: 'POST', body: formData,
            });
            const uploadData = await uploadRes.json();
            if (!uploadData.files || !uploadData.files[0]) throw new Error('이미지 업로드 실패');

            const saveRes = await fetch('/api/admin/slides', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label: slideLabel.trim(), image_url: uploadData.files[0].url }),
            });
            const saveData = await saveRes.json();
            if (saveData.success) {
                showMessage('success', `"${slideLabel}" 슬라이드가 추가되었습니다.`);
                setSlideLabel(''); setSlideFile(null); setCroppedPreview(null); setCropImage(null);
                const fi = document.getElementById('slideFile');
                if (fi) fi.value = '';
                loadSlides();
            } else throw new Error(saveData.message);
        } catch (err) {
            showMessage('error', err.message || '업로드 중 오류가 발생했습니다.');
        } finally { setSlideUploading(false); }
    };

    const handleSlideToggle = async (id) => {
        await fetch(`/api/admin/slides/${id}/toggle`, { method: 'PUT', credentials: 'include' });
        loadSlides();
    };

    const handleSlideHide = async (id, label) => {
        if (!window.confirm(`"${label}" 슬라이드를 숨기시겠습니까?`)) return;
        try {
            await fetch(`/api/admin/slides/${id}/toggle`, { method: 'PUT', credentials: 'include' });
            loadSlides();
            showMessage('success', `"${label}" 슬라이드가 숨겨졌습니다.`);
        } catch (err) {
            showMessage('error', '숨기기 실패');
        }
    };

    const handleMoveSlide = async (index, direction) => {
        const newSlides = [...slides];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSlides.length) return;
        [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
        const orders = newSlides.map((s, i) => ({ id: s.id, sort_order: i }));
        try {
            await fetch('/api/admin/slides/reorder', {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders }),
            });
            loadSlides();
        } catch (err) {
            showMessage('error', '순서 변경 실패');
        }
    };

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">슬라이드 관리</h1>
                    <p className="admin-page-desc">Business 페이지(Developer Center)에 노출되는 슬라이드 이미지를 관리합니다.</p>
                </div>
            </div>

            {message && (
                <div style={{
                    padding: '12px 16px', borderRadius: 10, marginBottom: 18,
                    background: message.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: message.type === 'success' ? '#86efac' : '#f87171',
                    border: `1px solid ${message.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                    fontSize: 14,
                }}>
                    {message.text}
                </div>
            )}

            {/* 업로드 폼 */}
            <div className="admin-card" style={{ padding: 22, marginBottom: 20 }}>
                <h2 style={{ color: '#fff', fontSize: '1rem', margin: '0 0 16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    + 새 슬라이드 추가
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                        <label style={labelStyle}>라벨 (표시 이름)</label>
                        <input type="text" placeholder="예: NewService" value={slideLabel} onChange={e => setSlideLabel(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>이미지 파일 (16:10 권장)</label>
                        <input id="slideFile" type="file" accept="image/*" onChange={handleFileSelect} style={{ ...inputStyle, padding: '9px 12px' }} />
                    </div>
                </div>
                {croppedPreview && (
                    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div>
                            <label style={{ ...labelStyle, marginBottom: 6 }}>크롭 결과 미리보기</label>
                            <img
                                src={croppedPreview}
                                alt="cropped preview"
                                style={{ display: 'block', maxWidth: 260, maxHeight: 160, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)' }}
                            />
                        </div>
                        <button
                            onClick={() => { setCropModalOpen(true); setCrop({ x: 0, y: 0 }); setZoom(1); }}
                            style={{ background: 'transparent', border: '1px solid rgba(1,188,214,0.4)', color: '#01BCD6', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
                        >다시 크롭</button>
                    </div>
                )}
                <button
                    onClick={handleSlideUpload}
                    disabled={slideUploading}
                    style={{
                        background: 'linear-gradient(135deg, #01BCD6, #0097a7)',
                        color: '#fff', border: 'none', padding: '10px 22px',
                        borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        opacity: slideUploading ? 0.5 : 1, fontFamily: 'inherit',
                        boxShadow: '0 4px 14px rgba(1, 188, 214, 0.25)',
                    }}
                >
                    {slideUploading ? '업로드 중...' : '슬라이드 추가'}
                </button>
            </div>

            {/* 슬라이드 목록 */}
            <div className="admin-card" style={{ padding: 22 }}>
                <h2 style={{ color: '#fff', fontSize: '1rem', margin: '0 0 16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    등록된 슬라이드 ({slides.length})
                </h2>
                {slides.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 40 }}>등록된 슬라이드가 없습니다.</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {slides.map((s, idx) => (
                            <div key={s.id} style={{
                                display: 'flex', alignItems: 'center', gap: 14,
                                background: 'rgba(7, 18, 35, 0.5)', borderRadius: 10,
                                padding: '10px 14px',
                                border: `1px solid ${s.is_active ? 'rgba(1, 188, 214, 0.18)' : 'rgba(255,255,255,0.05)'}`,
                                opacity: s.is_active ? 1 : 0.5,
                            }}>
                                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, width: 22, textAlign: 'center', fontFeatureSettings: '"tnum"' }}>{idx + 1}</span>
                                <img
                                    src={s.image_url}
                                    alt={s.label}
                                    style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                                        {s.label}
                                        {s.is_default && (
                                            <span style={{ marginLeft: 8, background: 'rgba(1,188,214,0.18)', color: '#8BD2F4', padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700 }}>기본</span>
                                        )}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {s.image_url}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <button
                                        onClick={() => handleMoveSlide(idx, 'up')}
                                        disabled={idx === 0}
                                        style={{ ...arrowBtnStyle, opacity: idx === 0 ? 0.2 : 1 }}
                                    >▲</button>
                                    <button
                                        onClick={() => handleMoveSlide(idx, 'down')}
                                        disabled={idx === slides.length - 1}
                                        style={{ ...arrowBtnStyle, opacity: idx === slides.length - 1 ? 0.2 : 1 }}
                                    >▼</button>
                                </div>
                                <button onClick={() => handleSlideToggle(s.id)} style={toggleBtnStyle(s.is_active)}>
                                    {s.is_active ? '활성' : '비활성'}
                                </button>
                                {s.is_active ? (
                                    <button onClick={() => handleSlideHide(s.id, s.label)}
                                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 13, padding: '4px 8px', fontFamily: 'inherit' }}>
                                        숨기기
                                    </button>
                                ) : (
                                    <span style={{ width: 46 }} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 크롭 모달 */}
            {cropModalOpen && cropImage && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.85)', zIndex: 9999,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                        슬라이드에 표시할 영역을 지정하세요 (16:10)
                    </div>
                    <div style={{ position: 'relative', width: '80vw', maxWidth: 600, height: '50vh', background: '#111', borderRadius: 12, overflow: 'hidden' }}>
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={16 / 10}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20 }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>줌</span>
                        <input type="range" min={1} max={3} step={0.1} value={zoom}
                            onChange={e => setZoom(Number(e.target.value))}
                            style={{ width: 160 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                        <button onClick={handleCropCancel}
                            style={{ padding: '10px 28px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
                            취소
                        </button>
                        <button onClick={handleCropConfirm}
                            style={{ padding: '10px 28px', borderRadius: 8, border: 'none', background: '#01BCD6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }}>
                            확인
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const labelStyle = {
    display: 'block', color: 'rgba(255,255,255,0.6)',
    fontSize: 12, marginBottom: 6, fontWeight: 500,
};

const inputStyle = {
    width: '100%', background: 'rgba(7, 18, 35, 0.7)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const toggleBtnStyle = (active) => ({
    background: active ? 'rgba(34, 197, 94, 0.18)' : 'rgba(239, 68, 68, 0.18)',
    color: active ? '#86efac' : '#f87171',
    border: `1px solid ${active ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
    padding: '4px 12px', borderRadius: 6,
    fontSize: 12, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
});

const arrowBtnStyle = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)', padding: '2px 8px', borderRadius: 4,
    cursor: 'pointer', fontSize: 10, fontFamily: 'inherit',
};
